import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Header from "./Header";
import {
  Card,
  CardHeader,
  CardDescription,
  CardTitle,
} from "./ui/card.jsx";
import PaymentHistoryTable from "./PaymentHistoryTable";
import SessionHistoryTable from "./SessionHistoryTable";

function CustomerDashboard() {
  const [totalPayments, setTotalPayments] = useState(0); // Total payment (added so far)
  const [totalExpense, setTotalExpense] = useState(0); // Total expense (cost of all sessions)
  const [balance, setBalance] = useState(0); // Current balance (total payment - total expense)
  const [totalVehicles, setTotalVehicles] = useState(0); // Total number of vehicles (sum of numVehicles)

  const [payments, setPayments] = useState([]); // Payment history
  const [sessions, setSessions] = useState([]); // Session history
  const [loading, setLoading] = useState(true);

  const userId = "zomato"; // Simulating logged-in customer ID

  // Fetch Customer Data (Payments and Sessions)
  useEffect(() => {
    const fetchCustomerData = async () => {
      setLoading(true);
      try {
        // Fetch Payments for the user
        const paymentsQuery = query(
          collection(db, "payments"),
          where("userId", "==", userId)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const paymentList = paymentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPayments(paymentList);

        // Calculate total payments
        const totalPaymentAmount = paymentList.reduce(
          (acc, payment) => acc + payment.amount,
          0
        );
        setTotalPayments(totalPaymentAmount);

        // Fetch Charging Sessions for the user
        const sessionsQuery = query(
          collection(db, "sessions"),
          where("userId", "==", userId)
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessionList = sessionsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSessions(sessionList);

        // Calculate total expense and total vehicles
        const totalSessionCost = sessionList.reduce(
          (acc, session) => acc + session.cost,
          0
        );
        const totalVehicleCount = sessionList.reduce(
          (acc, session) => acc + session.numVehicles,
          0
        ); // Sum of vehicles
        setTotalExpense(totalSessionCost);
        setTotalVehicles(totalVehicleCount);

        // Calculate current balance (totalPayments - totalExpense)
        setBalance(totalPaymentAmount - totalSessionCost);
      } catch (error) {
        console.error("Error fetching customer data: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Header />

      <div className="p-8 min-h-screen bg-neutral-100">
        <h2 className="text-5xl font-semibold mb-6 tracking-tighter">Dashboard</h2>

        {/* Dashboard Blocks */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-5">
          <Card className='bg-neutral-900 text-white'>
            <CardHeader>
              <CardDescription className='text-stone-300 font-medium text-base'>Total Payment</CardDescription>
              <CardTitle className='text-white text-4xl'><p>₹{totalPayments}</p></CardTitle>
            </CardHeader>
          </Card>

          <Card className='bg-neutral-900 text-white'>
            <CardHeader>
            <CardDescription className='text-stone-300 font-medium text-base'>Total Vehicles charged</CardDescription>
            <CardTitle className='text-white text-4xl'><p>{totalVehicles}</p></CardTitle>
            </CardHeader>
          </Card>

          <Card className='bg-neutral-900 text-white'>
            <CardHeader>
            <CardDescription className='text-stone-300 font-medium text-base'>Total Expense</CardDescription>
            <CardTitle className='text-white text-4xl'><p>₹{totalExpense}</p></CardTitle>
            </CardHeader>
          </Card>

          <Card className='bg-neutral-900 text-white'>
            <CardHeader>
            <CardDescription className='text-stone-300 font-medium text-base'>Current Balance</CardDescription>
            <CardTitle className='text-white text-4xl'><p>₹{balance}</p></CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Tables for Payment History and Session History */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          
          {/* Charging Session History Table */}
          <SessionHistoryTable data={sessions} />

          {/* Payment History Table */}
          <PaymentHistoryTable data={payments} />

        </div>
      </div>
    </>
  );
}

export default CustomerDashboard;
